import type { PublicationMedia } from "./types";

export type IconAlignment = "bottom" | "center" | "top";

export function parseIconOptions(
  label: string,
): { scale: number; alignment: IconAlignment } | null {
  const match = /^icon(?::(\d+(?:\.\d+)?))?(?::(bottom|center|top))?$/i.exec(
    label,
  );
  if (!match) return null;
  const scale = Number(match[1] || 1);
  return scale >= 0.1 && scale <= 10
    ? {
        scale,
        alignment: (match[2]?.toLowerCase() || "bottom") as IconAlignment,
      }
    : null;
}

export function parseIconScale(label: string): number | null {
  return parseIconOptions(label)?.scale ?? null;
}

export function createInlineIcon(
  source: string,
  scale = 1,
  alignment: IconAlignment = "bottom",
): string {
  if (parseIconScale(`icon:${scale}`) === null)
    throw new Error("图标大小需在 0.1 到 10 倍之间");
  return `![icon${scale === 1 ? "" : `:${scale}`}${alignment === "bottom" ? "" : `:${alignment}`}](${source.replace(/\(/g, "%28").replace(/\)/g, "%29")})`;
}

export function findInlineIcon(source: string, start: number, end = start) {
  for (const match of source.matchAll(/!\[([^\]]*)\]\(([^)\n]+)\)/g)) {
    const options = parseIconOptions(match[1]);
    const stop = match.index + match[0].length;
    if (options && start >= match.index && end <= stop)
      return { start: match.index, end: stop, source: match[2], ...options };
  }
  return null;
}

export function renderMarkdown(
  markdown: string,
  media: PublicationMedia[] = [],
): string {
  const mediaUrls = new Map(
    media.map((item) => [item.id.toLowerCase(), item.url]),
  );
  const lines = String(markdown || "")
    .replace(/\r\n?/g, "\n")
    .split("\n");
  const output: string[] = [];
  let list: "ul" | "ol" | null = null;
  let codeLanguage: string | null = null;
  const alignments: Array<"center" | "right"> = [];
  const closeList = () => {
    if (list) output.push(`</${list}>`);
    list = null;
  };

  for (const rawLine of lines) {
    if (codeLanguage !== null && rawLine.trim().startsWith("```")) {
      closeList();
      output.push("</code></pre></div>");
      codeLanguage = null;
      continue;
    }
    if (codeLanguage !== null) {
      output.push(`${escapeHtml(rawLine)}\n`);
      continue;
    }
    const alignment = /^\s*:::\s*(center|right)\s*$/.exec(rawLine);
    if (alignment || (/^\s*:::\s*$/.test(rawLine) && alignments.length)) {
      closeList();
      if (alignment) {
        alignments.push(alignment[1] as "center" | "right");
        output.push(`<div style="text-align:${alignment[1]}">`);
      } else {
        alignments.pop();
        output.push("</div>");
      }
      continue;
    }
    const fence = rawLine.trim().match(/^```\s*([^\s`]*)/);
    if (fence) {
      closeList();
      codeLanguage = fence[1] || "plaintext";
      output.push(
        `<div class="markdown-code-block"><div class="markdown-code-header"><span>${escapeHtml(codeLanguage)}</span></div><pre><code>`,
      );
      continue;
    }
    const heading = rawLine.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      closeList();
      const level = heading[1].length;
      output.push(`<h${level}>${inline(heading[2], mediaUrls)}</h${level}>`);
      continue;
    }
    const unordered = rawLine.match(/^\s*[-*]\s+(.+)$/);
    const ordered = rawLine.match(/^\s*\d+\.\s+(.+)$/);
    if (unordered || ordered) {
      const nextList = unordered ? "ul" : "ol";
      if (list !== nextList) {
        closeList();
        list = nextList;
        output.push(`<${list}>`);
      }
      output.push(
        `<li>${inline((unordered || ordered)?.[1] || "", mediaUrls)}</li>`,
      );
      continue;
    }
    closeList();
    const quote = rawLine.match(/^>\s?(.+)$/);
    if (quote) {
      output.push(`<blockquote>${inline(quote[1], mediaUrls)}</blockquote>`);
    } else if (rawLine.trim()) {
      output.push(`<p>${inline(rawLine, mediaUrls)}</p>`);
    }
  }
  closeList();
  if (codeLanguage !== null) output.push("</code></pre></div>");
  output.push(...alignments.map(() => "</div>"));
  return output.join("");
}

export function wrapMarkdownAlignment(
  source: string,
  selectionStart: number,
  selectionEnd: number,
  alignment: "center" | "right",
) {
  const start = Math.max(0, Math.min(selectionStart, source.length));
  const end = Math.max(start, Math.min(selectionEnd, source.length));
  const lineStart = start === 0 ? 0 : source.lastIndexOf("\n", start - 1) + 1;
  const lineEnd = source.indexOf(
    "\n",
    end > start && source[end - 1] === "\n" ? end - 1 : end,
  );
  const stop = lineEnd < 0 ? source.length : lineEnd;
  const selected = source.slice(lineStart, stop) || "文字";
  const opening = `::: ${alignment}\n`;
  return {
    value: `${source.slice(0, lineStart)}${opening}${selected}\n:::${source.slice(stop)}`,
    selectionStart: lineStart + opening.length,
    selectionEnd: lineStart + opening.length + selected.length,
  };
}

export function safeIconSource(source: string): string {
  const value = source.trim();
  if (/[\s<>"'`\\\u0000-\u001f]/.test(value)) return "";
  if (/^https?:\/\/[^/\s]+(?:\/[^\s]*)?$/i.test(value)) return value;
  return /^\/?(?:[\w.-]+\/)*[\w.-]+\.(?:svg|png|jpe?g|gif|webp)(?:[?#][^\s]*)?$/i.test(
    value,
  )
    ? value
    : "";
}

export function wrapMarkdownCodeBlock(
  source: string,
  selectionStart: number,
  selectionEnd: number,
): { value: string; selectionStart: number; selectionEnd: number } {
  const start = Math.max(0, Math.min(selectionStart, source.length));
  const end = Math.max(start, Math.min(selectionEnd, source.length));
  const selected = source.slice(start, end);
  const code = selected || "代码";
  const leadingBreak = start > 0 && source[start - 1] !== "\n" ? "\n" : "";
  const trailingBreak = end < source.length && source[end] !== "\n" ? "\n" : "";
  const opening = `${leadingBreak}\`\`\`\n`;
  const closing = `${code.endsWith("\n") ? "" : "\n"}\`\`\`${trailingBreak}`;

  return {
    value: `${source.slice(0, start)}${opening}${code}${closing}${source.slice(end)}`,
    selectionStart: start + opening.length,
    selectionEnd: start + opening.length + code.length,
  };
}

function inline(value: string, mediaUrls: Map<string, string>): string {
  const tokens: string[] = [];
  const token = (html: string) => `\u0000${tokens.push(html) - 1}\u0000`;
  let result = value.replace(/`([^`\n]+)`/g, (_match, code: string) =>
    token(`<code>${escapeHtml(code)}</code>`),
  );
  result = result.replace(
    /!\[([^\]]*)\]\(([^)\n]+)\)/gi,
    (_match, alt: string, source: string) => {
      const options = parseIconOptions(alt);
      const icon = options !== null;
      const verticalAlign =
        options?.alignment === "center"
          ? "middle"
          : `text-${options?.alignment || "bottom"}`;
      const iconAttributes = icon
        ? `class="markdown-inline-icon" style="width:${options!.scale}em;height:${options!.scale}em;vertical-align:${verticalAlign}" `
        : "";
      const media = /^media:\/\/([0-9a-f-]{36})$/i.exec(source.trim());
      const url = media
        ? mediaUrls.get(media[1].toLowerCase())
        : icon
          ? safeIconSource(source)
          : "";
      return url
        ? token(
            `<img ${iconAttributes}src="${escapeAttribute(url)}" alt="${icon ? "" : escapeAttribute(alt)}" loading="lazy">`,
          )
        : token(
            icon
              ? `<span ${iconAttributes}>□</span>`
              : `<span class="markdown-image-pending">图片正在等待发布</span>`,
          );
    },
  );
  // Preserve deliberate gaps without changing ordinary word wrapping.
  result = escapeHtml(result).replace(/ {2,}/g, (spaces) =>
    "\u00a0".repeat(spaces.length),
  );
  result = result.replace(
    /\[([^\]]+)\]\(contact:\/\/customer-service\)/gi,
    (_match, label: string) =>
      token(`<span class="markdown-contact-link">${label}</span>`),
  );
  result = result.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/gi,
    (_match, label: string, url: string) =>
      token(`<a href="${url}" target="_blank" rel="noreferrer">${label}</a>`),
  );
  result = result.replace(/`([^`]+)`/g, "<code>$1</code>");
  result = result.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  result = result.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  result = result.replace(/(^|[^*])\*([^*]+)\*/g, "$1$2");
  result = result.replace(/(^|[^\w])_([^_]+)_(?!\w)/g, "$1$2");
  return result.replace(
    /\u0000(\d+)\u0000/g,
    (_match, index: string) => tokens[Number(index)] || "",
  );
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replaceAll("`", "&#96;");
}
